import {Inject, Injectable, NgZone, OnDestroy, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router} from '@angular/router';
import {Store} from '@ngxs/store';
import {BehaviorSubject, Subscription} from 'rxjs';
import type Tracker from '@openreplay/tracker/class';
import type {ReplayAssist, SupportStatus} from './session-replay-assist';
import {environment} from '@env/environment';
import {SesionState, SesionStateModel} from '@app/state/sesion/sesion.state';

export interface ReplayIdentity {
    userID: string;
    metadata: Record<string, string>;
}

export function replayIdentity(state: SesionStateModel, application: string, device: string): ReplayIdentity | null {
    const user = state?.login?.user;
    const company = state?.companySelected || user?.empresa;
    if (!state?.login?.accessToken || !user?.id || !company?.ruc) {
        return null;
    }
    const text = (value: unknown) => String(value ?? '').slice(0, 255);
    return {
        userID: `${text(company.ruc)}:${text(user.id)}`,
        metadata: {
            application,
            environment: environment.glitchTipEnvironment,
            ruc: text(company.ruc),
            razon_social: text(company.razonSocial),
            usuario: text(user.username || user.nombreCompleto || user.name || `${user.nombres || ''} ${user.apellidos || ''}`.trim()),
            usuario_id: text(user.id),
            equipo_id: device,
            punto_venta: text(state.office?.nombre || state.office?.nombreCorto || state.office?.codigo)
        }
    };
}

@Injectable({providedIn: 'root'})
export class SessionReplayService implements OnDestroy {
    private readonly supportStatus = new BehaviorSubject<SupportStatus>(null);
    readonly supportStatus$ = this.supportStatus.asObservable();
    private assist?: ReplayAssist;
    private readonly subscriptions = new Subscription();
    private tracker: Tracker;
    private desired: ReplayIdentity | null = null;
    private identityKey = '';
    private revision = 0;
    private running = false;
    private initialized = false;
    private destroyed = false;
    private device = '';
    private timer: ReturnType<typeof setTimeout>;
    private navigatingToPublic = false;

    constructor(@Inject(PLATFORM_ID) private platformId: Object,
                private store: Store, private router: Router, private zone: NgZone) {}

    startMonitoring(): void {
        if (this.initialized || !isPlatformBrowser(this.platformId) || !environment.openReplayEnabled) {
            return;
        }
        this.initialized = true;
        try {
            this.device = this.deviceId();
            this.subscriptions.add(this.store.select(SesionState).subscribe(() => this.updateIdentity()));
            this.subscriptions.add(this.router.events.subscribe(event => {
                if (event instanceof NavigationStart && this.publicRoute(event.url)) {
                    this.navigatingToPublic = true;
                    this.setIdentity(null);
                } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
                    this.navigatingToPublic = false;
                    this.updateIdentity();
                }
            }));
            this.updateIdentity();
        } catch {
            // Recording must never interfere with authentication or sales.
            this.setIdentity(null);
        }
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.subscriptions.unsubscribe();
        clearTimeout(this.timer);
        this.setIdentity(null);
    }

    stopSupport(): void {
        try {
            this.assist?.stop();
        } catch { /* A support cleanup error must not interrupt a sale. */
        } finally {
            this.zone.run(() => this.supportStatus.next('paused'));
        }
    }

    resumeSupport(): void {
        if (this.desired && this.assist && !this.destroyed) {
            try {
                this.assist.start(); // Viewing and control require fresh permission.
                this.zone.run(() => this.supportStatus.next(null));
            } catch { /* Support availability must not interrupt a sale. */ }
        }
    }

    private updateIdentity(): void {
        const state = this.store.selectSnapshot(SesionState);
        this.setIdentity(!this.navigatingToPublic && this.router.navigated && !this.publicRoute(this.router.url)
            ? replayIdentity(state, environment.openReplayApplication, this.device) : null);
    }

    private setIdentity(identity: ReplayIdentity | null): void {
        const key = JSON.stringify(identity);
        if (key === this.identityKey) {
            return;
        }
        this.identityKey = key;
        this.desired = identity;
        this.revision++;
        this.stop(); // Also cancels a pending SDK start on logout/account change.
        clearTimeout(this.timer);
        if (identity && !this.destroyed) {
            this.zone.runOutsideAngular(() => {
                this.timer = setTimeout(() => { void this.reconcile(); }, 0);
            });
        }
    }

    private async reconcile(): Promise<void> {
        if (this.running) {
            return;
        }
        this.running = true;
        const revision = this.revision;
        try {
            if (!this.tracker) {
                this.tracker = await this.loadTracker();
            }
            if (revision !== this.revision || !this.desired || this.destroyed) {
                return;
            }
            await this.tracker.start({...this.desired, forceNew: true});
            if (revision !== this.revision || this.destroyed) {
                this.stop();
            } else {
                try {
                    this.assist?.start();
                } catch { /* Replay and sales remain usable without live support. */ }
            }
        } catch {
            this.stop();
        } finally {
            this.running = false;
            if (revision !== this.revision && this.desired && !this.destroyed) {
                void this.reconcile();
            }
        }
    }

    private async loadTracker(): Promise<Tracker> {
        const [sdk, privacy] = await Promise.all([
            import('@openreplay/tracker/class'), import('./session-replay-privacy')
        ]);
        const tracker = new sdk.default(privacy.replayOptions(environment.openReplayProjectKey));
        tracker.use(app => {
            if (app) {
                privacy.protectReplayAttributes(app);
            }
        });
        try {
            const {attachReplayAssist} = await import('./session-replay-assist');
            this.assist = attachReplayAssist(tracker, value => this.zone.run(() => this.supportStatus.next(value)));
        } catch { /* Blocked storage, unsupported browsers or Assist failures do not disable replay. */ }
        return tracker;
    }

    private stop(): void {
        try {
            this.assist?.stop();
        } catch { /* Always stop the recorder even if support cleanup fails. */ }
        this.zone?.run(() => this.supportStatus.next(null));
        try {
            this.tracker?.stop();
        } catch {
            // Storage, extensions or the replay server may be unavailable.
        }
    }

    private publicRoute(url: string): boolean {
        return /^\/(login|logout)(?:[/?#;]|$)/.test(url);
    }

    private deviceId(): string {
        const key = `${environment.openReplayApplication}.replay.device`;
        try {
            const stored = localStorage.getItem(key);
            if (stored && /^[a-f0-9-]{36}$/i.test(stored)) {
                return stored;
            }
        } catch { /* The sale remains usable with blocked storage. */ }
        const id = crypto.randomUUID();
        try {
            localStorage.setItem(key, id);
        } catch { /* This browser will receive a new ID after reload. */ }
        return id;
    }
}
